import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { lettersAPI, RISK_COLOR, errText } from '../lib/lettersAPI';

const Step = ({ n, title, children }) => (
  <Card data-testid={`wizard-step-${n}`}><CardHeader className="pb-2"><CardTitle className="text-base">{n}. {title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card>
);

export default function NewLetter() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [entities, setEntities] = useState([]);
  const [f, setF] = useState({ client_id: '', entity_id: '', template_id: '', answers: {}, recipient: {} });
  const [q, setQ] = useState('');
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/api/clients').then((r) => setClients(Array.isArray(r.data) ? r.data : r.data.clients || r.data.items || [])).catch(() => {});
    lettersAPI.templates({ active_only: true }).then(setTemplates).catch(() => {});
  }, []);
  useEffect(() => { if (f.client_id) lettersAPI.entities(f.client_id).then(setEntities).catch(() => setEntities([])); }, [f.client_id]);
  const tpl = templates.find((t) => t.id === f.template_id);
  useEffect(() => {
    if (!f.client_id || !f.template_id) return;
    const t = setTimeout(() => lettersAPI.resolve(f).then((r) => setFields(r.fields)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [f]);
  const shown = useMemo(() => templates.filter((t) => !q || `${t.name} ${t.category} ${t.description}`.toLowerCase().includes(q.toLowerCase())), [templates, q]);
  const setAns = (k, patch) => setF({ ...f, answers: { ...f.answers, [k]: { ...(f.answers[k] || {}), ...patch } } });
  const create = async () => {
    setBusy(true);
    try { const lt = await lettersAPI.create(f); toast.success('Draft letter created'); navigate(`/letters/${lt.id}`); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6" data-testid="new-letter-page">
      <Button variant="ghost" onClick={() => navigate('/letters')}><ArrowLeft className="mr-2 h-4 w-4" />Accountants Letters</Button>
      <h1 className="text-3xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Create letter</h1>
      <Step n={1} title="Client and entity">
        <div className="grid gap-2 md:grid-cols-2">
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value, entity_id: '' })} data-testid="wizard-client">
            <option value="">Select client…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}</select>
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.entity_id} onChange={(e) => setF({ ...f, entity_id: e.target.value })} disabled={!entities.length} data-testid="wizard-entity">
            {entities.map((e) => <option key={e.id} value={e.type === 'client' && e.id === f.client_id ? '' : e.id}>{e.name} ({e.entity_type})</option>)}</select>
        </div>
      </Step>
      <Step n={2} title="Template">
        <Input placeholder="Search by name or category" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" data-testid="wizard-template-search" />
        {!templates.length && <p className="text-sm text-muted-foreground" data-testid="wizard-no-templates">No approved templates yet – an owner/admin must approve templates under Letter Templates.</p>}
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {shown.map((t) => (
            <button key={t.id} type="button" onClick={() => setF({ ...f, template_id: t.id, answers: {} })} data-testid={`wizard-template-${t.seed_key || t.id}`}
              className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${f.template_id === t.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
              <span className="flex-1"><b>{t.name}</b><span className="block text-xs text-muted-foreground">{t.category} · {t.description}</span></span>
              <Badge className={RISK_COLOR[t.risk]}>{t.risk}</Badge></button>))}
        </div>
        {tpl?.risk === 'opinion-restricted' && <p className="mt-2 flex items-center gap-2 text-sm text-red-700" data-testid="wizard-opinion-warning"><AlertTriangle className="h-4 w-4" />Opinion-restricted: needs two approvals (the second by an owner/admin).</p>}
      </Step>
      {tpl && <Step n={3} title="Recipient and template questions">
        <div className="grid gap-2 md:grid-cols-2">
          {tpl.third_party && ['name', 'organisation', 'address', 'email'].map((k) => (
            <Input key={k} placeholder={`Recipient ${k}`} value={f.recipient[k] || ''} onChange={(e) => setF({ ...f, recipient: { ...f.recipient, [k]: e.target.value } })} data-testid={`wizard-recipient-${k}`} />))}
          {tpl.questions.map((qq) => (
            <div key={qq.key} className="space-y-1">
              <Input placeholder={qq.label} type={qq.type === 'number' ? 'number' : 'text'} value={f.answers[qq.key]?.value || ''} onChange={(e) => setAns(qq.key, { value: e.target.value })} data-testid={`wizard-q-${qq.key}`} />
              {qq.financial && <Input placeholder="Evidence reference (e.g. NOA 2024-25)" value={f.answers[qq.key]?.evidence_ref || ''} onChange={(e) => setAns(qq.key, { evidence_ref: e.target.value })} data-testid={`wizard-q-${qq.key}-evidence`} />}
            </div>))}
        </div>
        {!!tpl.evidence.length && <p className="mt-2 text-xs text-muted-foreground">Evidence required: {tpl.evidence.join('; ')} (attach on the next screen).</p>}
      </Step>}
      {tpl && <Step n={4} title="Auto-populated fields">
        <div className="grid gap-1 text-sm md:grid-cols-2" data-testid="wizard-fields">
          {fields.map((x) => (
            <div key={x.key} className={`flex justify-between rounded px-2 py-1 ${x.missing ? 'bg-red-50 text-red-800' : 'bg-muted/40'}`} data-testid={`wizard-field-${x.key}`}>
              <span className="font-mono text-xs">{`{{${x.key}}}`}</span><span>{x.system ? 'Assigned on issue' : x.missing ? 'MISSING' : x.value}</span></div>))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Fields are filled only from stored records. Financial figures are never auto-filled – enter them with an evidence reference. Missing fields block finalisation.</p>
        <Button className="mt-3" disabled={busy || !f.client_id} onClick={create} data-testid="wizard-create">Generate editable letter</Button>
      </Step>}
    </div>
  );
}
